import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'
import {
  applyCopilotReviewEffortResponse,
  captureCopilotReviewEffort,
  changesCopilotReviewEffortSubmission,
  collectCopilotReviewEffort,
  copilotReviewEffortFieldNames,
  copilotReviewEffortPickerSelector,
  enhanceCopilotReviewEffortPicker,
  getCopilotReviewEffortContext,
  hasNewerCopilotReviewEffortChoice,
  isCopilotReviewEffortResponse,
  isCurrentCopilotReviewEffort,
  isRelatedCopilotReviewEffort,
  preserveCopilotReviewEffortState,
  recordCopilotReviewEffortSelection,
  updateCopilotReviewEffortResponseState,
  type CopilotReviewEffortSubmission,
} from '@github-ui/pull-request-page-data-tooling/copilot-review-effort'
import {showGlobalError} from '../behaviors/ajax-error'

const effortSubmissions = new WeakMap<HTMLFormElement, CopilotReviewEffortSubmission>()
const sidebarEffortPickers = new WeakMap<Element, Map<string, Element>>()
const pickerLocations = new WeakMap<Element, {scope: Element; context: string}>()

type SidebarRequestState = {submitted: number; applied: number; appliedResponseRevision: number}
type CopilotReviewEffortSidebarSubmission = {
  effort: CopilotReviewEffortSubmission
  requestNumber: number
  requestState: SidebarRequestState
}

const sidebarRequests = new WeakMap<CopilotReviewEffortSubmission['state'], SidebarRequestState>()

function recordCopilotReviewEffortSubmission(event: FormDataEvent) {
  const form = event.currentTarget as HTMLFormElement
  const submission = captureCopilotReviewEffort(form.querySelector(copilotReviewEffortPickerSelector))
  if (submission) {
    effortSubmissions.set(form, submission)
  } else {
    effortSubmissions.delete(form)
  }
}

on('formdata', 'form.js-issue-sidebar-form', recordCopilotReviewEffortSubmission, {capture: true})

export function takeCopilotReviewEffortSubmission(form: HTMLFormElement, body: RequestInit['body']) {
  const submission = body instanceof FormData ? effortSubmissions.get(form) : undefined
  effortSubmissions.delete(form)
  if (!submission) return

  const requestState = sidebarRequests.get(submission.state) ?? {submitted: 0, applied: 0, appliedResponseRevision: 0}
  requestState.submitted += 1
  sidebarRequests.set(submission.state, requestState)
  return {effort: submission, requestNumber: requestState.submitted, requestState}
}

type SidebarReconciliationResult =
  | {status: 'replace'; form: HTMLFormElement; replacement: DocumentFragment}
  | {status: 'ignored'}
  | {status: 'invalid'}

function getRelatedCopilotReviewEffortForm(
  form: HTMLFormElement,
  submission: CopilotReviewEffortSubmission | undefined,
) {
  if (submission && !isRelatedCopilotReviewEffort(submission)) return
  const currentForm = submission?.picker.closest('form') ?? form
  if (currentForm.isConnected) return currentForm
}

function isLatestCopilotReviewEffortSidebarSubmission(submission: CopilotReviewEffortSidebarSubmission | undefined) {
  return !submission || submission.requestNumber === submission.requestState.submitted
}

export function shouldShowCopilotReviewEffortError(
  form: HTMLFormElement,
  sidebarSubmission: CopilotReviewEffortSidebarSubmission | undefined,
) {
  return (
    getRelatedCopilotReviewEffortForm(form, sidebarSubmission?.effort) !== undefined &&
    isLatestCopilotReviewEffortSidebarSubmission(sidebarSubmission)
  )
}

export function reconcileCopilotReviewEffortResponse(
  form: HTMLFormElement,
  sidebarSubmission: CopilotReviewEffortSidebarSubmission | undefined,
  response: {html: DocumentFragment},
): SidebarReconciliationResult {
  const submission = sidebarSubmission?.effort
  const currentForm = getRelatedCopilotReviewEffortForm(form, submission)
  if (!currentForm) return {status: 'ignored'}
  const replaceSidebar = !sidebarSubmission || sidebarSubmission.requestNumber > sidebarSubmission.requestState.applied
  if (!replaceSidebar && submission && !isCurrentCopilotReviewEffort(submission)) return {status: 'ignored'}

  const replacement = response.html
  if (
    submission &&
    !reconcileSubmittedPicker(submission, replacement, replaceSidebar, sidebarSubmission?.requestState)
  ) {
    return {
      status: replaceSidebar && isLatestCopilotReviewEffortSidebarSubmission(sidebarSubmission) ? 'invalid' : 'ignored',
    }
  }
  if (!replaceSidebar) return {status: 'ignored'}
  if (sidebarSubmission) sidebarSubmission.requestState.applied = sidebarSubmission.requestNumber
  return {status: 'replace', form: currentForm, replacement}
}

function reconcileSubmittedPicker(
  submission: CopilotReviewEffortSubmission,
  replacement: DocumentFragment,
  replaceSidebar: boolean,
  requestState?: SidebarRequestState,
) {
  const candidates = Array.from(replacement.querySelectorAll(copilotReviewEffortPickerSelector))
  const previousPicker = submission.picker
  if (candidates.length === 0) {
    if (!replaceSidebar) return true
    const legacyPicker = Array.from(replacement.querySelectorAll('[data-copilot-review-effort-picker]')).find(
      candidate => !candidate.matches(copilotReviewEffortPickerSelector),
    )
    if (legacyPicker) {
      unregisterCopilotReviewEffortPicker(previousPicker)
      return true
    }
    const containers = findCopilotReviewEffortContainers(replacement, submission.context)
    const [container] = containers
    if (
      !container ||
      containers.length !== 1 ||
      !preserveCopilotReviewEffortInHiddenInputs(previousPicker, container, submission.context)
    ) {
      return false
    }
    unregisterCopilotReviewEffortPicker(previousPicker)
    return true
  }

  const matching = candidates.filter(candidate => getCopilotReviewEffortContext(candidate) === submission.context)
  const [replacementPicker] = matching
  if (!replacementPicker || matching.length !== 1) return false
  const fields = collectCopilotReviewEffort(replacementPicker)
  if (!isCopilotReviewEffortResponse(fields)) return false

  if (!replaceSidebar) {
    // An older request can acknowledge a choice without restoring older reviewer HTML.
    applyCopilotReviewEffortResponse(submission, fields)
    return true
  }
  // Revisions the sidebar already knows about; anything beyond them came from another flow and outranks this response.
  const knownResponseRevision = Math.max(requestState?.appliedResponseRevision ?? 0, submission.responseRevision)
  // An echo of our own pre-acceptance fields must not undo an acceptance that already landed.
  const echoesConsumedSubmission =
    submission.state.responseRevision > submission.responseRevision &&
    !changesCopilotReviewEffortSubmission(submission, fields)
  if (
    isRelatedCopilotReviewEffort(submission) &&
    !hasNewerCopilotReviewEffortChoice(submission) &&
    submission.state.responseRevision === knownResponseRevision &&
    !echoesConsumedSubmission
  ) {
    updateCopilotReviewEffortResponseState(submission, replacementPicker, fields)
    if (requestState) requestState.appliedResponseRevision = submission.state.responseRevision
  } else if (preserveCopilotReviewEffortState(previousPicker, replacementPicker) === 'incomplete') {
    return false
  }
  unregisterCopilotReviewEffortPicker(previousPicker)
  enhanceCopilotReviewEffortPicker(replacementPicker)
  return true
}

export function preserveCopilotReviewEffortInPreview(picker: Element | null, replacement: DocumentFragment) {
  const replacementPicker = replacement.querySelector(copilotReviewEffortPickerSelector)
  if (
    picker &&
    replacementPicker &&
    getCopilotReviewEffortContext(picker) === getCopilotReviewEffortContext(replacementPicker, picker.closest('form'))
  ) {
    return preserveCopilotReviewEffortState(picker, replacementPicker)
  }
}

function findCopilotReviewEffortContainers(scope: ParentNode, context: string) {
  const containers = Array.from(scope.querySelectorAll('.js-issue-sidebar-form'))
  if (scope instanceof Element && scope.matches('.js-issue-sidebar-form')) containers.unshift(scope)
  return containers.filter(container => {
    const form = container instanceof HTMLFormElement ? container : container.closest('form')
    return form && getCopilotReviewEffortContext(container, form) === context
  })
}

function preserveCopilotReviewEffortInHiddenInputs(picker: Element, container: Element, context: string) {
  if (copilotReviewEffortFieldNames.some(name => container.querySelector(`input[name="${name}"]`))) return

  const hiddenPicker = picker.ownerDocument.createElement('div')
  hiddenPicker.hidden = true
  hiddenPicker.setAttribute('data-copilot-review-effort-picker', '')
  hiddenPicker.setAttribute('data-copilot-review-effort-version', '1')
  container.append(hiddenPicker)
  if (getCopilotReviewEffortContext(hiddenPicker) !== context) {
    hiddenPicker.remove()
    return
  }
  const result = preserveCopilotReviewEffortState(picker, hiddenPicker)
  if (result !== 'preserved') {
    hiddenPicker.remove()
    if (result === 'incomplete') showGlobalError()
    return
  }
  return hiddenPicker
}

function registerCopilotReviewEffortPicker(picker: Element, location: {scope: Element; context: string}) {
  let pickers = sidebarEffortPickers.get(location.scope)
  if (!pickers) {
    pickers = new Map()
    sidebarEffortPickers.set(location.scope, pickers)
  }
  pickers.set(location.context, picker)
  pickerLocations.set(picker, location)
}

function unregisterCopilotReviewEffortPicker(picker: Element) {
  const location = pickerLocations.get(picker)
  if (!location) return
  const pickers = sidebarEffortPickers.get(location.scope)
  if (pickers?.get(location.context) === picker) pickers.delete(location.context)
  pickerLocations.delete(picker)
}

observe(copilotReviewEffortPickerSelector, {
  add(picker) {
    enhanceCopilotReviewEffortPicker(picker)
    const scope = picker.closest('.js-discussion-sidebar-item')?.parentElement
    if (!scope) return

    const context = getCopilotReviewEffortContext(picker)
    const previous = sidebarEffortPickers.get(scope)?.get(context)
    if (previous && previous !== picker) {
      if (previous.isConnected) return
      if (preserveCopilotReviewEffortState(previous, picker) === 'incomplete') showGlobalError()
      unregisterCopilotReviewEffortPicker(previous)
    }
    registerCopilotReviewEffortPicker(picker, {scope, context})
  },
  remove(picker) {
    if (picker.isConnected) return
    const location = pickerLocations.get(picker)
    if (!location) return
    const replacement = Array.from(location.scope.querySelectorAll(copilotReviewEffortPickerSelector)).find(
      candidate => candidate !== picker && getCopilotReviewEffortContext(candidate) === location.context,
    )
    if (replacement) {
      if (preserveCopilotReviewEffortState(picker, replacement) === 'incomplete') showGlobalError()
      unregisterCopilotReviewEffortPicker(picker)
      registerCopilotReviewEffortPicker(replacement, location)
      return
    }
    const containers = findCopilotReviewEffortContainers(location.scope, location.context).filter(
      container => container.isConnected,
    )
    const [container] = containers
    if (container && containers.length === 1) {
      preserveCopilotReviewEffortInHiddenInputs(picker, container, location.context)
    }
    unregisterCopilotReviewEffortPicker(picker)
  },
})

function markCopilotReviewEffortExplicitlySelected(event: CustomEvent<{item: HTMLElement}>) {
  const picker = event.currentTarget as HTMLElement
  recordCopilotReviewEffortSelection(picker, event.detail.item)
}

on('itemActivated', '[data-copilot-review-effort-picker]', markCopilotReviewEffortExplicitlySelected)
